import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  Truck, Plus, MapPin, Loader2, Clock, CheckCircle2,
  ArrowRight, Star, Search, Shield, Award, MessageCircle,
  Phone, ChevronDown, ChevronUp, X, User, Filter,
  CheckCheck, AlertCircle, FileCheck, BadgeCheck
} from "lucide-react";

import Sidebar  from "../Includes/Sidebar";
import TopBar   from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

// ─── Constants ────────────────────────────────────────────────────────────────

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

// All US state codes — used for cert filter chips
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

// Cert types the company can require — matches backend cert model
const CERT_TYPES = [
  { key: "ppca",        label: "PPCA Certified",     icon: "🛡️" },
  { key: "dot",         label: "DOT Compliant",       icon: "📋" },
  { key: "cdl_a",       label: "CDL Class A",         icon: "🚛" },
  { key: "escort_cert", label: "Escort Certified",    icon: "🔒" },
  { key: "hazmat",      label: "HazMat Endorsed",     icon: "☣️" },
  { key: "oversize",    label: "Oversize Permit",     icon: "📏" },
];

// Driver status badge colors
const DRIVER_STATUS = {
  qualified:   { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  label: "Qualified"    },
  pending:     { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  label: "Pending Review"},
  accepted:    { bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   label: "Accepted"     },
  rejected:    { bg: "bg-red-50",    text: "text-red-600",    border: "border-red-200",    label: "Rejected"     },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d, opts = { weekday:"short", month:"short", day:"numeric" }) =>
  d ? new Date(d).toLocaleDateString("en-US", opts) : "—";

const fmt2 = (n) => parseFloat(n || 0).toFixed(2);

// ─── Status Pill ──────────────────────────────────────────────────────────────

function StatusPill({ status, isOpen }) {
  if (isOpen) return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
      <Clock className="w-2.5 h-2.5"/> Pending Match
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 whitespace-nowrap">
      <CheckCircle2 className="w-2.5 h-2.5"/> Assigned
    </span>
  );
}

// ─── Cert Badge ───────────────────────────────────────────────────────────────

function CertBadge({ label, icon, verified = true }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-semibold border ${
      verified
        ? "bg-green-50 text-green-700 border-green-200"
        : "bg-gray-50 text-gray-400 border-gray-200 opacity-60"
    }`}>
      <span>{icon}</span> {label}
      {verified && <CheckCheck className="w-2.5 h-2.5"/>}
    </span>
  );
}

// ─── Driver Applicant Card ────────────────────────────────────────────────────
// Shows one driver who applied. Has Accept / Reject / Message / Call buttons.

function DriverApplicantCard({ driver, requiredCerts = [], requiredStates = [], onMessage, onCall, onAccept, onReject }) {
  const [expanded, setExpanded] = useState(false);

  // Check which required certs this driver has
  const certStatus = requiredCerts.map(cert => ({
    ...cert,
    has: driver.certifications?.includes(cert.key) ?? false,
  }));

  // Check which required states this driver is certified in
  const stateStatus = requiredStates.map(state => ({
    state,
    has: driver.certified_states?.includes(state) ?? false,
  }));

  const missingCerts   = certStatus.filter(c => !c.has).length;
  const missingStates  = stateStatus.filter(s => !s.has).length;
  const fullyQualified = missingCerts === 0 && missingStates === 0;

  const statusKey = driver.application_status || (fullyQualified ? "qualified" : "pending");
  const statusStyle = DRIVER_STATUS[statusKey] || DRIVER_STATUS.pending;

  return (
    <div className={`bg-white rounded-xl border ${statusKey === "accepted" ? "border-blue-300" : "border-gray-100"} shadow-sm overflow-hidden transition-all`}>
      {/* ── Driver header row ── */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm flex-shrink-0 ${
          fullyQualified ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
        }`}>
          {driver.initials || driver.name?.slice(0,2).toUpperCase() || "DR"}
        </div>

        {/* Name + rating + vehicle */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-gray-900">{driver.name}</span>
            {fullyQualified && (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                <BadgeCheck className="w-2.5 h-2.5"/> VERIFIED
              </span>
            )}
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
              {statusStyle.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5 text-amber-400"/> {driver.rating || "—"}
            </span>
            <span className="text-[10px] text-gray-300">•</span>
            <span className="text-[10px] text-gray-400">{driver.vehicle || "Pilot Car"}</span>
            <span className="text-[10px] text-gray-300">•</span>
            <span className="text-[10px] text-gray-400">{driver.location || "Location N/A"}</span>
          </div>
        </div>

        {/* Action buttons — right side */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Message */}
          <button
            onClick={() => onMessage(driver)}
            className="w-8 h-8 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-700 transition-colors"
            title="Message driver"
          >
            <MessageCircle className="w-3.5 h-3.5"/>
          </button>
          {/* Call */}
          {driver.phone && (
            <button
              onClick={() => onCall(driver)}
              className="w-8 h-8 rounded-lg bg-green-50 hover:bg-green-100 border border-green-200 flex items-center justify-center text-green-700 transition-colors"
              title="Call driver"
            >
              <Phone className="w-3.5 h-3.5"/>
            </button>
          )}
          {/* Expand toggle */}
          <button
            onClick={() => setExpanded(e => !e)}
            className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-500 transition-colors"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
          </button>
        </div>
      </div>

      {/* ── Qualification summary bar ── */}
      <div className={`px-4 py-2 border-t ${fullyQualified ? "bg-green-50 border-green-100" : missingCerts + missingStates > 2 ? "bg-red-50 border-red-100" : "bg-amber-50 border-amber-100"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {fullyQualified
              ? <><Shield className="w-3 h-3 text-green-600"/><span className="text-[10px] font-bold text-green-700">All requirements met</span></>
              : <><AlertCircle className="w-3 h-3 text-amber-600"/><span className="text-[10px] font-bold text-amber-700">{missingCerts + missingStates} requirement{missingCerts + missingStates !== 1 ? "s" : ""} missing</span></>
            }
          </div>
          {requiredCerts.length > 0 && (
            <div className="flex gap-1 flex-wrap justify-end">
              {certStatus.slice(0, 3).map(c => (
                <span key={c.key} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${c.has ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {c.icon} {c.label.split(" ")[0]}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Expanded detail ── */}
      {expanded && (
        <div className="px-4 py-3 border-t border-gray-100 space-y-3 bg-gray-50 animate-in slide-in-from-top-2 duration-150">

          {/* Certifications */}
          {requiredCerts.length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Required Certifications</p>
              <div className="flex flex-wrap gap-1.5">
                {certStatus.map(c => (
                  <CertBadge key={c.key} label={c.label} icon={c.icon} verified={c.has}/>
                ))}
              </div>
            </div>
          )}

          {/* State certifications */}
          {requiredStates.length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Required State Certs</p>
              <div className="flex flex-wrap gap-1">
                {stateStatus.map(s => (
                  <span key={s.state} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                    s.has ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-600 border-red-200"
                  }`}>
                    {s.state} {s.has ? "✓" : "✗"}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Driver details */}
          <div className="grid grid-cols-2 gap-2 bg-white rounded-xl border border-gray-100 p-3">
            {[
              { label:"Experience",  val: driver.years_exp ? `${driver.years_exp} yrs` : "—" },
              { label:"Total Loads", val: driver.total_loads ?? "—" },
              { label:"Response",    val: driver.response_time || "Fast" },
              { label:"Applied",     val: fmtDate(driver.applied_at) },
            ].map(({ label, val }) => (
              <div key={label}>
                <p className="text-[9px] text-gray-400 font-medium">{label}</p>
                <p className="text-xs font-semibold text-gray-800 mt-0.5">{val}</p>
              </div>
            ))}
          </div>

          {/* Driver note */}
          {driver.note && (
            <div className="bg-white rounded-xl border border-gray-100 p-3">
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1">Driver Note</p>
              <p className="text-xs text-gray-600 leading-relaxed">{driver.note}</p>
            </div>
          )}

          {/* Accept / Reject actions */}
          {statusKey !== "accepted" && statusKey !== "rejected" && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onAccept(driver)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5"/> Accept Driver
              </button>
              <button
                onClick={() => onReject(driver)}
                className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-1.5"
              >
                <X className="w-3.5 h-3.5"/> Decline
              </button>
            </div>
          )}
          {statusKey === "accepted" && (
            <div className="flex items-center justify-center gap-1.5 py-2 bg-blue-50 border border-blue-200 rounded-xl">
              <CheckCheck className="w-3.5 h-3.5 text-blue-600"/>
              <span className="text-xs font-bold text-blue-700">Driver accepted — load assigned</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Load Detail + Applicants Modal ──────────────────────────────────────────
// Full modal — shows load info, required cert/state filters, and driver list

function LoadDetailModal({ load, onClose, onMessageDriver, onCallDriver }) {
  const [tab,             setTab]           = useState("applicants"); // "details" | "applicants" | "requirements"
  const [drivers,         setDrivers]       = useState([]);
  const [driverLoading,   setDriverLoading] = useState(true);
  const [driverSearch,    setDriverSearch]  = useState("");
  const [driverFilter,    setDriverFilter]  = useState("all"); // all | qualified | pending
  const [requiredCerts,   setRequiredCerts] = useState(
    load._required_certs || []
  );
  const [requiredStates,  setRequiredStates]  = useState(
    load._required_states || []
  );
  const [savingReqs, setSavingReqs] = useState(false);

  const token = localStorage.getItem("user_token");

  // Fetch applicants for this load
  useEffect(() => {
    if (tab !== "applicants") return;
    const go = async () => {
      try {
        setDriverLoading(true);
        const res = await fetch(`${API_URL}/api/loads/${load.id}/applicants/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDrivers(data.results || data || []);
        } else {
          // Fallback mock data for development
          setDrivers(MOCK_DRIVERS);
        }
      } catch {
        setDrivers(MOCK_DRIVERS);
      } finally {
        setDriverLoading(false);
      }
    };
    go();
  }, [load.id, tab, token]);

  const saveRequirements = async () => {
    setSavingReqs(true);
    try {
      await fetch(`${API_URL}/api/loads/${load.id}/requirements/`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ required_certs: requiredCerts, required_states: requiredStates }),
      });
    } catch (e) { console.warn(e); }
    finally { setSavingReqs(false); }
  };

  const handleAccept = async (driver) => {
    try {
      await fetch(`${API_URL}/api/loads/${load.id}/accept-driver/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ driver_id: driver.id }),
      });
      setDrivers(ds => ds.map(d => d.id === driver.id ? { ...d, application_status: "accepted" } : d));
    } catch (e) { console.warn(e); }
  };

  const handleReject = async (driver) => {
    try {
      await fetch(`${API_URL}/api/loads/${load.id}/reject-driver/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ driver_id: driver.id }),
      });
      setDrivers(ds => ds.map(d => d.id === driver.id ? { ...d, application_status: "rejected" } : d));
    } catch (e) { console.warn(e); }
  };

  const toggleCert  = (k) => setRequiredCerts(p  => p.includes(k)  ? p.filter(x=>x!==k)  : [...p, k]);
  const toggleState = (s) => setRequiredStates(p => p.includes(s)  ? p.filter(x=>x!==s)  : [...p, s]);

  const isOpen = load.status?.toLowerCase() === "open";

  const filteredDrivers = drivers.filter(d => {
    const matchFilter =
      driverFilter === "all"       ? true :
      driverFilter === "qualified" ? (d.certifications || []).length >= requiredCerts.length :
      d.application_status === "pending" || !d.application_status;
    const matchSearch = !driverSearch ||
      d.name?.toLowerCase().includes(driverSearch.toLowerCase()) ||
      d.location?.toLowerCase().includes(driverSearch.toLowerCase());
    return matchFilter && matchSearch;
  });

  const qualifiedCount = drivers.filter(d => {
    const hasCerts   = requiredCerts.every(c  => d.certifications?.includes(c));
    const hasStates  = requiredStates.every(s => d.certified_states?.includes(s));
    return hasCerts && hasStates;
  }).length;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col max-h-[92vh] sm:max-h-[88vh] overflow-hidden">

        {/* ── Modal header ── */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ background: "#1a2744" }}>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-white">{load.origin} → {load.destination}</h2>
              <StatusPill status={load.status} isOpen={isOpen}/>
            </div>
            <p className="text-[10px] text-white/50 mt-0.5">#{load.id} · {fmtDate(load.departure_date)}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Applicant count badge */}
            {drivers.length > 0 && (
              <div className="bg-white/15 border border-white/20 rounded-lg px-2.5 py-1 text-center">
                <p className="text-[9px] text-white/60 font-medium">Applicants</p>
                <p className="text-sm font-bold text-white leading-tight">{drivers.length}</p>
              </div>
            )}
            {qualifiedCount > 0 && (
              <div className="bg-green-500/30 border border-green-400/40 rounded-lg px-2.5 py-1 text-center">
                <p className="text-[9px] text-green-200 font-medium">Qualified</p>
                <p className="text-sm font-bold text-green-100 leading-tight">{qualifiedCount}</p>
              </div>
            )}
            <button onClick={onClose} className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white text-lg transition-colors">
              ×
            </button>
          </div>
        </div>

        {/* ── Tab bar ── */}
        <div className="flex border-b border-gray-200 bg-white flex-shrink-0">
          {[
            { key: "applicants",   label: "Applicants",   count: drivers.length },
            { key: "requirements", label: "Requirements", count: requiredCerts.length + requiredStates.length },
            { key: "details",      label: "Load Details", count: null },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-3 text-xs font-semibold transition-colors border-b-2 flex items-center justify-center gap-1.5 ${
                tab === t.key ? "border-blue-700 text-blue-700" : "border-transparent text-gray-400 hover:text-gray-700"
              }`}
            >
              {t.label}
              {t.count != null && t.count > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${tab === t.key ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"}`}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="flex-1 overflow-y-auto">

          {/* APPLICANTS TAB */}
          {tab === "applicants" && (
            <div className="p-4 space-y-3">
              {/* Search + filter bar */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2"/>
                  <input
                    value={driverSearch} onChange={e => setDriverSearch(e.target.value)}
                    placeholder="Search drivers…"
                    className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="flex gap-1">
                  {["all","qualified","pending"].map(f => (
                    <button key={f} onClick={() => setDriverFilter(f)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-semibold capitalize transition-all border ${
                        driverFilter === f ? "text-white border-transparent" : "bg-white text-gray-500 border-gray-200"
                      }`}
                      style={driverFilter === f ? { background: "#1a2744" } : {}}
                    >{f}</button>
                  ))}
                </div>
              </div>

              {/* Requirement summary chip */}
              {(requiredCerts.length > 0 || requiredStates.length > 0) && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl text-[10px] text-blue-700 font-medium flex-wrap">
                  <Shield className="w-3 h-3 flex-shrink-0"/>
                  <span>Filtering for:</span>
                  {requiredCerts.map(k => {
                    const ct = CERT_TYPES.find(c => c.key === k);
                    return <span key={k} className="bg-blue-100 px-1.5 py-0.5 rounded font-bold">{ct?.icon} {ct?.label}</span>;
                  })}
                  {requiredStates.map(s => (
                    <span key={s} className="bg-blue-100 px-1.5 py-0.5 rounded font-bold">{s}</span>
                  ))}
                </div>
              )}

              {/* Driver cards */}
              {driverLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500"/>
                </div>
              ) : filteredDrivers.length === 0 ? (
                <div className="text-center py-12">
                  <User className="w-10 h-10 text-gray-300 mx-auto mb-3"/>
                  <p className="text-sm font-semibold text-gray-700 mb-1">No applicants yet</p>
                  <p className="text-xs text-gray-400 max-w-xs mx-auto leading-relaxed">
                    Drivers who apply for this load will appear here. You can add certification requirements to pre-filter qualified drivers.
                  </p>
                  <button onClick={() => setTab("requirements")} className="mt-4 text-xs text-blue-600 font-semibold hover:underline">
                    Set requirements →
                  </button>
                </div>
              ) : (
                filteredDrivers.map(driver => (
                  <DriverApplicantCard
                    key={driver.id}
                    driver={driver}
                    requiredCerts={CERT_TYPES.filter(c => requiredCerts.includes(c.key))}
                    requiredStates={requiredStates}
                    onMessage={onMessageDriver}
                    onCall={onCallDriver}
                    onAccept={handleAccept}
                    onReject={handleReject}
                  />
                ))
              )}
            </div>
          )}

          {/* REQUIREMENTS TAB */}
          {tab === "requirements" && (
            <div className="p-4 space-y-5">
              {/* Certification requirements */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-blue-700"/>
                  <h3 className="text-sm font-bold text-gray-900">Required Certifications</h3>
                </div>
                <p className="text-xs text-gray-400 mb-3 leading-relaxed">
                  Only drivers who hold all selected certifications will be flagged as "Qualified" for this load.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CERT_TYPES.map(cert => {
                    const active = requiredCerts.includes(cert.key);
                    return (
                      <button
                        key={cert.key}
                        onClick={() => toggleCert(cert.key)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left transition-all text-xs font-semibold ${
                          active
                            ? "border-blue-700 bg-blue-50 text-blue-800"
                            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                        }`}
                      >
                        <span className="text-base flex-shrink-0">{cert.icon}</span>
                        <span className="truncate">{cert.label}</span>
                        {active && <CheckCheck className="w-3 h-3 text-blue-600 ml-auto flex-shrink-0"/>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* State certification requirements */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-4 h-4 text-blue-700"/>
                  <h3 className="text-sm font-bold text-gray-900">Required State Certifications</h3>
                </div>
                <p className="text-xs text-gray-400 mb-3 leading-relaxed">
                  Select the states that the driver must be PPCA/PEVO certified in to qualify for this load.
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-52 overflow-y-auto pr-1">
                  {US_STATES.map(state => {
                    const active = requiredStates.includes(state);
                    return (
                      <button
                        key={state}
                        onClick={() => toggleState(state)}
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-all ${
                          active
                            ? "border-blue-700 bg-blue-600 text-white"
                            : "border-gray-200 bg-white text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        {state}
                      </button>
                    );
                  })}
                </div>

                {/* Route auto-suggest */}
                {(load.origin || load.destination) && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-[9px] font-bold text-amber-700 uppercase tracking-wider mb-1.5">Route Quick-Add</p>
                    <p className="text-[10px] text-amber-700 mb-2">Auto-select states from your route:</p>
                    <div className="flex gap-1.5 flex-wrap">
                      {[load.origin, load.destination]
                        .filter(Boolean)
                        .map(city => {
                          const stateCode = city.split(", ").pop();
                          if (!US_STATES.includes(stateCode)) return null;
                          return (
                            <button
                              key={stateCode}
                              onClick={() => !requiredStates.includes(stateCode) && toggleState(stateCode)}
                              className={`text-[10px] font-bold px-2 py-1 rounded-lg border transition-colors ${
                                requiredStates.includes(stateCode)
                                  ? "bg-blue-600 text-white border-blue-600"
                                  : "bg-white text-amber-700 border-amber-300 hover:bg-amber-100"
                              }`}
                            >
                              {requiredStates.includes(stateCode) ? "✓ " : "+ "}{stateCode}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>

              {/* Save button */}
              <button
                onClick={saveRequirements}
                disabled={savingReqs}
                className="w-full text-white font-bold py-3 rounded-xl text-sm transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: "#1a2744" }}
              >
                {savingReqs ? <Loader2 className="w-4 h-4 animate-spin"/> : <FileCheck className="w-4 h-4"/>}
                {savingReqs ? "Saving…" : "Save Requirements"}
              </button>
            </div>
          )}

          {/* DETAILS TAB */}
          {tab === "details" && (
            <div className="p-4 space-y-4">
              {/* Route */}
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Route</p>
                <div className="flex items-center gap-3">
                  <p className="text-base font-black text-gray-900 truncate">{load.origin}</p>
                  <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0" strokeWidth={2.5}/>
                  <p className="text-base font-black text-gray-900 truncate">{load.destination}</p>
                </div>
                <p className="text-xs text-gray-400 mt-1">📅 {fmtDate(load.departure_date, { weekday:"long", year:"numeric", month:"long", day:"numeric" })}</p>
              </div>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-xl border border-gray-100 p-4">
                {[
                  { label:"Commodity",  val: load.commodity || "—" },
                  { label:"Weight",     val: load.weight    || "N/A" },
                  { label:"Dimensions", val: `${load.width||"—"} × ${load.height||"—"} × ${load.length||"—"}` },
                  { label:"Fee Paid",   val: `$${fmt2(load.posting_fee)}`, green:true },
                ].map(({ label, val, green }) => (
                  <div key={label}>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
                    <p className={`text-sm font-semibold ${green ? "text-green-600" : "text-gray-900"}`}>{val}</p>
                  </div>
                ))}
              </div>

              {/* Escort requirements */}
              <div>
                <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-2">Escort Requirements</p>
                <div className="flex flex-wrap gap-2">
                  {load.needs_front_escort && <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">Front Escort</span>}
                  {load.needs_chase_escort && <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-100">Chase Escort</span>}
                  {load.needs_height_pole  && <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">Height Pole</span>}
                  {load.needs_steersman   && <span className="px-2.5 py-1 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-semibold border border-indigo-100">Steersman</span>}
                  {!load.needs_front_escort && !load.needs_chase_escort && !load.needs_height_pole && !load.needs_steersman && (
                    <span className="text-xs italic text-gray-400">Standard Escort Only</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between gap-3 flex-shrink-0 bg-white">
          <button onClick={onClose} className="border border-gray-200 text-gray-700 text-sm font-medium px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
            Close
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setTab("requirements")}
              className="text-xs font-bold px-3 py-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex items-center gap-1.5"
            >
              <Filter className="w-3 h-3"/> Set Requirements
            </button>
            <Link to="/post-load">
              <button className="text-xs font-bold px-3 py-2 rounded-xl text-white flex items-center gap-1.5 transition-colors hover:opacity-90" style={{ background: "#1a2744" }}>
                <Plus className="w-3 h-3"/> Post Similar
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Message Driver Modal ─────────────────────────────────────────────────────

function MessageDriverModal({ driver, load, onClose }) {
  const [message, setMessage] = useState(
    `Hi ${driver?.name?.split(" ")[0] || "there"},\n\nI'm reviewing your application for the load from ${load?.origin} to ${load?.destination}.\n\n`
  );
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const token = localStorage.getItem("user_token");

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await fetch(`${API_URL}/api/tradelink/chat/send/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ recipient_id: driver.id, message, load_id: load?.id }),
      });
      setSent(true);
    } catch (e) { console.warn(e); }
    finally { setSending(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden">

        <div className="flex items-center justify-between px-5 py-4" style={{ background: "#1a2744" }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center font-bold text-white text-sm flex-shrink-0">
              {driver?.initials || driver?.name?.slice(0,2).toUpperCase() || "DR"}
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Message {driver?.name?.split(" ")[0]}</h3>
              <p className="text-[10px] text-white/50">Re: {load?.origin} → {load?.destination}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-xl">×</button>
        </div>

        {sent ? (
          <div className="px-5 py-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3"/>
            <p className="font-bold text-gray-900 mb-1">Message Sent!</p>
            <p className="text-sm text-gray-400">
              {driver?.name} will be notified via the TradeLink messaging system.
            </p>
            <button onClick={onClose} className="mt-5 text-sm font-bold text-white px-5 py-2.5 rounded-xl" style={{ background: "#1a2744" }}>
              Done
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Driver info strip */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <User className="w-4 h-4 text-gray-400 flex-shrink-0"/>
              <div>
                <p className="text-xs font-semibold text-gray-800">{driver?.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-gray-400">⭐ {driver?.rating || "—"}</span>
                  <span className="text-[10px] text-gray-300">•</span>
                  <span className="text-[10px] text-gray-400">{driver?.vehicle || "Pilot Car"}</span>
                  {driver?.phone && (
                    <>
                      <span className="text-[10px] text-gray-300">•</span>
                      <span className="text-[10px] text-gray-400">{driver.phone}</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Message box */}
            <div>
              <label className="text-[9px] font-bold uppercase tracking-widest text-gray-400 block mb-1.5">Your Message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={6}
                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
                placeholder="Type your message…"
              />
            </div>

            {/* Quick reply chips */}
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">Quick Messages</p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  "Can you confirm your availability?",
                  "Please share your insurance documents.",
                  "What is your current location?",
                  "Are you available for a call?",
                ].map(q => (
                  <button
                    key={q}
                    onClick={() => setMessage(m => m + q + "\n")}
                    className="text-[10px] bg-gray-100 text-gray-600 hover:bg-gray-200 px-2 py-1 rounded-lg transition-colors"
                  >
                    + {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-1">
              <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-700 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                onClick={send}
                disabled={!message.trim() || sending}
                className="flex-1 text-white text-sm font-bold py-2.5 rounded-xl transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ background: "#1a2744" }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <MessageCircle className="w-4 h-4"/>}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Load Card ────────────────────────────────────────────────────────────────

function LoadCard({ load, onOpen }) {
  const isOpen = load.status?.toLowerCase() === "open";
  const applicantCount = load.applicant_count || 0;
  const qualifiedCount = load.qualified_count || 0;

  return (
    <div
      onClick={() => onOpen(load)}
      className="bg-white border border-gray-100 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
    >
      {/* Status + badges */}
      <div className="flex flex-wrap gap-1.5 mb-2.5">
        <StatusPill status={load.status} isOpen={isOpen}/>
        {load.is_featured && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700">
            <Star className="w-2.5 h-2.5"/> Featured
          </span>
        )}
        {/* Applicant count chip */}
        {applicantCount > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <User className="w-2.5 h-2.5"/> {applicantCount} applied
            {qualifiedCount > 0 && <span className="text-green-600">· {qualifiedCount} qualified</span>}
          </span>
        )}
      </div>

      {/* Route */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-sm font-bold text-gray-900 truncate">{load.origin}</span>
        <ArrowRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" strokeWidth={2.5}/>
        <span className="text-sm font-bold text-gray-900 truncate">{load.destination}</span>
      </div>

      {/* Meta */}
      <p className="text-xs text-gray-400 flex items-center gap-1.5">
        <Truck className="w-3 h-3"/>
        {load.commodity}
        <span className="text-gray-200">•</span>
        {fmtDate(load.departure_date)}
      </p>

      {/* Bottom row */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <div>
          <p className="text-[9px] text-gray-400 font-medium uppercase tracking-wider">Marketplace Fee</p>
          <p className="text-sm font-bold text-gray-900 mt-0.5">${fmt2(load.posting_fee)}</p>
        </div>
        <button
          onClick={e => { e.stopPropagation(); onOpen(load); }}
          className="text-xs font-bold px-3 py-1.5 rounded-xl transition-all"
          style={isOpen
            ? { border:"1px solid #e5e7eb", background:"#fff", color:"#374151" }
            : { background:"#1a2744", color:"#fff" }
          }
        >
          {applicantCount > 0 ? `View ${applicantCount} Applicants` : isOpen ? "View Status" : "Driver Info"}
        </button>
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center border-2 border-dashed border-gray-200 rounded-2xl bg-white col-span-full">
      <div className="w-16 h-16 rounded-full bg-gray-50 flex items-center justify-center mb-4">
        <MapPin className="w-8 h-8 text-gray-300"/>
      </div>
      <h3 className="text-base font-bold text-gray-800 mb-2">No Loads Found</h3>
      <p className="text-sm text-gray-400 max-w-sm leading-relaxed mb-6">
        You haven't posted any loads matching this filter yet, or your load history is empty.
      </p>
      <Link to="/post-load">
        <button className="text-white text-sm font-bold px-5 py-3 rounded-xl flex items-center gap-2 hover:opacity-90 transition-opacity" style={{ background:"#1a2744" }}>
          <Plus className="w-4 h-4"/> Post Your First Load
        </button>
      </Link>
    </div>
  );
}

// ─── Mock driver data (used when API not yet connected) ───────────────────────

const MOCK_DRIVERS = [
  {
    id: 1, name: "Marcus Reed", initials: "MR", rating: 4.9,
    vehicle: "Kenworth T680", location: "Houston, TX", phone: "+1-713-555-0192",
    years_exp: 8, total_loads: 142, response_time: "< 1 hr",
    applied_at: new Date().toISOString(), application_status: "pending",
    certifications: ["ppca","dot","cdl_a","escort_cert"],
    certified_states: ["TX","LA","OK","AR","NM","AZ"],
    note: "Available immediately. Fully insured, PPCA certified since 2016. Have all permits for oversize loads in TX and surrounding states.",
  },
  {
    id: 2, name: "Tanya Brooks", initials: "TB", rating: 4.7,
    vehicle: "Peterbilt 579", location: "Dallas, TX", phone: "+1-972-555-0147",
    years_exp: 5, total_loads: 87, response_time: "< 2 hrs",
    applied_at: new Date(Date.now()-3600000).toISOString(), application_status: "pending",
    certifications: ["ppca","cdl_a"],
    certified_states: ["TX","OK"],
    note: "Experienced with wind turbine components and heavy machinery. Available from June 30.",
  },
  {
    id: 3, name: "Jake Moreno", initials: "JM", rating: 4.5,
    vehicle: "Freightliner Cascadia", location: "San Antonio, TX", phone: null,
    years_exp: 3, total_loads: 34, response_time: "< 4 hrs",
    applied_at: new Date(Date.now()-7200000).toISOString(), application_status: "pending",
    certifications: ["ppca"],
    certified_states: ["TX"],
    note: "",
  },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function MyLoads() {
  const navigate = useNavigate();

  const user  = (() => { try { return JSON.parse(localStorage.getItem("user")); } catch { return null; } })();
  const token = localStorage.getItem("user_token");

  const [loads,         setLoads]         = useState([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [activeNav,     setActiveNav]     = useState("loads");
  const [loadFilter,    setLoadFilter]    = useState("all");
  const [searchQuery,   setSearchQuery]   = useState("");
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [notifCount,    setNotifCount]    = useState(2);

  // Modal states
  const [selectedLoad,    setSelectedLoad]    = useState(null); // opens LoadDetailModal
  const [messageTarget,   setMessageTarget]   = useState(null); // { driver, load }
  const [callTarget,      setCallTarget]      = useState(null); // driver (for confirm dialog)

  // Auth guard
  useEffect(() => {
    if (!user || !token) navigate("/company-login", { replace: true });
  }, [user, token, navigate]);

  // Fetch loads
  useEffect(() => {
    if (!user?.email || !token) { setIsLoading(false); return; }
    const go = async () => {
      try {
        const res = await fetch(`${API_URL}/api/loads/company/?email=${user.email}`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (res.ok) setLoads(data.results || []);
      } catch (err) { console.error(err); }
      finally { setIsLoading(false); }
    };
    go();
  }, [user?.email, token]);

  const companyName = user?.company_name || user?.name || "Company";
  const initials    = companyName.split(" ").slice(0,2).map(w=>w[0]).join("").toUpperCase() || "CO";

  const activePostings = loads.filter(l=>l.status?.toLowerCase()==="open").length;
  const assignedLoads  = loads.filter(l=>l.status?.toLowerCase()!=="open").length;
  const totalSpent     = loads.reduce((a,l)=>a+parseFloat(l.posting_fee||0),0);

  const filteredLoads = loads.filter(load => {
    const matchFilter =
      loadFilter==="all"      ? true :
      loadFilter==="open"     ? load.status?.toLowerCase()==="open" :
      load.status?.toLowerCase()!=="open";
    const q = searchQuery.toLowerCase();
    const matchSearch = !q ||
      (load.origin       && load.origin.toLowerCase().includes(q))       ||
      (load.destination  && load.destination.toLowerCase().includes(q))  ||
      (load.commodity    && load.commodity.toLowerCase().includes(q))    ||
      (load.id           && load.id.toString().includes(q));
    return matchFilter && matchSearch;
  });

  const handleMessage = (driver) => {
    setMessageTarget({ driver, load: selectedLoad });
  };

  const handleCall = (driver) => {
    if (driver.phone) window.open(`tel:${driver.phone}`);
  };

  if (!user || !token) return null;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden" style={{ fontFamily:"Inter,system-ui,sans-serif" }}>
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={()=>setSidebarOpen(false)}/>
      )}

      <Sidebar
        activeNav={activeNav} setActiveNav={setActiveNav}
        sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        initials={initials} companyName={companyName}
        loads={loads} activePostings={activePostings}
        assignedLoads={assignedLoads} totalSpent={totalSpent}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar
          title="My Loads"
          setSidebarOpen={setSidebarOpen}
          initials={initials}
          notifCount={notifCount}
          setNotifCount={setNotifCount}
        />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">

            {/* Page header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Manage Postings</h1>
                <p className="text-sm text-gray-500 mt-1">View, filter, and manage applicants for each dispatched load.</p>
              </div>
              <Link to="/post-load" className="flex-shrink-0">
                <button
                  className="w-full sm:w-auto flex items-center justify-center gap-2 text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:-translate-y-0.5 transition-all"
                  style={{ background:"#1a2744", boxShadow:"0 4px 14px 0 rgba(26,39,68,0.2)" }}
                >
                  <Plus className="w-4 h-4"/> Post New Load
                </button>
              </Link>
            </div>

            {/* Filter + Search bar */}
            <div className="bg-white p-2 rounded-2xl border border-gray-200 shadow-sm flex flex-col sm:flex-row gap-2">
              <div className="flex gap-1 overflow-x-auto p-1">
                {[
                  { key:"all",      label:"All Loads"      },
                  { key:"open",     label:"Pending Match"  },
                  { key:"assigned", label:"Assigned"       },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={()=>setLoadFilter(f.key)}
                    className="flex-shrink-0 px-4 py-2 rounded-xl text-xs font-semibold transition-all"
                    style={loadFilter===f.key ? {background:"#1a2744",color:"#fff"} : {background:"transparent",color:"#6b7280"}}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="hidden sm:block w-px bg-gray-100 mx-1"/>
              <div className="flex-1 relative flex items-center">
                <Search className="w-4 h-4 text-gray-400 absolute left-3"/>
                <input
                  type="text"
                  placeholder="Search by city, state, or commodity…"
                  value={searchQuery}
                  onChange={e=>setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-blue-100 outline-none placeholder:text-gray-400"
                />
              </div>
            </div>

            {/* Stats summary */}
            {loads.length > 0 && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label:"Total Loads", val:loads.length, color:"text-gray-900" },
                  { label:"Pending Match", val:activePostings, color:"text-amber-600" },
                  { label:"Assigned", val:assignedLoads, color:"text-green-600" },
                ].map(s=>(
                  <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-4 py-3 text-center shadow-sm">
                    <p className={`text-xl font-bold ${s.color}`}>{s.val}</p>
                    <p className="text-[10px] text-gray-400 font-medium mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Load grid */}
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500"/>
                <p className="text-sm text-gray-400 font-medium">Retrieving your loads…</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredLoads.length === 0
                  ? <EmptyState/>
                  : filteredLoads.map(load => (
                      <LoadCard key={load.id} load={load} onOpen={setSelectedLoad}/>
                    ))
                }
              </div>
            )}
          </div>
        </main>

        <BottomNav activeNav={activeNav} setActiveNav={setActiveNav}/>
      </div>

      {/* Load Detail + Applicants Modal */}
      {selectedLoad && (
        <LoadDetailModal
          load={selectedLoad}
          onClose={()=>setSelectedLoad(null)}
          onMessageDriver={driver => setMessageTarget({ driver, load:selectedLoad })}
          onCallDriver={handleCall}
        />
      )}

      {/* Message Driver Modal */}
      {messageTarget && (
        <MessageDriverModal
          driver={messageTarget.driver}
          load={messageTarget.load}
          onClose={()=>setMessageTarget(null)}
        />
      )}
    </div>
  );
}
